import _ from "lodash";
import { isIchOrganization } from "../constants/ichOrganizations";
import { canPrintJabLabel } from "./jabJobDetails";

/**
 * Cloud reports an embedded Price List can ask Cloud to print: the customer's own Status/History
 * reports. Cloud renders them from the saved job with the Price List's current pricing applied, so the
 * printout matches the screen. Who may print each is decided as on Status/History.
 *
 * ICH's Invoice is the player's Options Report: Cloud opens the job in the player on the invoice's Print
 * Options with the Price List's lines (actions.showInvoicePrintModal), as Status/History's Print Invoice does.
 */
export const ICH_INVOICE_HOST_REPORT_ID = "ich-invoice";
export const ICH_HOST_REPORTS = Object.freeze([
	Object.freeze({ id: ICH_INVOICE_HOST_REPORT_ID, label: "Invoice" }),
	Object.freeze({ id: "ich-selections-portfolio-2", label: "Portfolio" })
]);

// JAB's job label (Status/History "Print Label"), for JAB's administrators and internal staff only.
export const JAB_HOST_REPORTS = Object.freeze([
	Object.freeze({ id: "cc-label", label: "Label" })
]);

// The Price List draft fields each report reads. The label's task groups come from the lines and its
// description, instructions and notes from the job details; the ICH reports read the lines only.
const REPORT_DRAFT_FIELDS = Object.freeze({
	"cc-label": Object.freeze(["invoiceItems", "jobDetails"])
});
const DEFAULT_DRAFT_FIELDS = Object.freeze(["invoiceItems"]);

/**
 * The Cloud reports offered to the viewer.
 *
 * @param {string} orgId The organization printing.
 * @param {Object} [access] The viewer's role: { administrator, internalStaff }. JAB's Label needs one of them.
 */
export function getHostReportsForOrg(orgId, access = {}) {
	if (isIchOrganization(orgId)) {
		return ICH_HOST_REPORTS;
	}
	return canPrintJabLabel(orgId, access) ? JAB_HOST_REPORTS : [];
}

export function findHostReportForOrg(orgId, reportId, access = {}) {
	const id = _.toString(reportId).trim();
	return getHostReportsForOrg(orgId, access).find(report => report.id === id) || null;
}

const isPlainObject = value => Boolean(value && _.isPlainObject(value));

/**
 * Validate and clone the transient pricing an embedded Price List sends with a report request. Only
 * the fields the report reads are taken (the invoice items, and the job details for the JAB label);
 * the activity, customer, company, job and S3 identity remain owned by AmbiFi Cloud.
 *
 * @param {*} pricing Candidate request.pricing value.
 * @param {string} [reportId] The requested report; without one, only the invoice items are taken.
 * @returns {Object|null} A detached draft, or null when invalid.
 */
export function normalizeHostReportDraft(pricing, reportId) {
	if (!isPlainObject(pricing)
		|| !Array.isArray(pricing.invoiceItems)
		|| !pricing.invoiceItems.every(isPlainObject)) {
		return null;
	}
	const draft = { invoiceItems: _.cloneDeep(pricing.invoiceItems) };
	const fields = REPORT_DRAFT_FIELDS[_.toString(reportId).trim()] || DEFAULT_DRAFT_FIELDS;
	if (fields.includes("jobDetails") && pricing.jobDetails !== undefined) {
		if (!isPlainObject(pricing.jobDetails)) {
			return null;
		}
		draft.jobDetails = _.cloneDeep(pricing.jobDetails);
	}
	return draft;
}

// The search hit fields the Status/History label prints (persistence/s3.js buildJabLabelReportData).
const LABEL_HIT_FIELDS = Object.freeze([
	"name", "sessionNameResolved", "jobId", "jobIdString", "projectId", "company", "projectGroup", "displayName",
	"profilePicture", "dueDate", "createdDateTimeIsoString", "lastUpdatedDateTimeIsoString", "dueDateIsoString",
	"workflowStatus"
]);
const hasValue = value => value !== undefined && value !== null && value !== "";

/**
 * The hit the embedded Price List's Label is printed with. Status/History print the label with the
 * search hit the job was opened from; here that is the hit the Price List was launched from, kept in
 * Cloud's pricing state, completed from the saved job. Nothing comes from the Price List.
 *
 * @param {Object} launchSource appState.showPricing.source (the launching search hit, when it matches the job).
 * @param {Object} savedJob The verified session's job (appState.showPricing.checklist).
 * @param {string} url The verified session's S3 key.
 * @returns {Object} A detached hit.
 */
export function buildHostLabelHit(launchSource, savedJob, url) {
	const source = isPlainObject(launchSource) ? launchSource : {};
	const job = isPlainObject(savedJob) ? savedJob : {};
	const hit = LABEL_HIT_FIELDS.reduce((result, field) => {
		const value = hasValue(source[field]) ? source[field] : job[field];
		return value === undefined ? result : { ...result, [field]: _.cloneDeep(value) };
	}, {});
	const notes = [source.notes, job.notes].find(Array.isArray) || [];
	return {
		...hit,
		sessionNameResolved: hasValue(hit.sessionNameResolved) ? hit.sessionNameResolved : (job.sessionName || ""),
		instanceId: job.instanceId || source.instanceId || "",
		url,
		// The label's photos; its thumbnails read the notes as an array.
		notes: _.cloneDeep(notes)
	};
}

/**
 * Overlay a validated draft onto a Cloud-owned activity.
 *
 * @param {Object} activity The saved job read from S3.
 * @param {Object} draft The Price List draft (see normalizeHostReportDraft).
 * @param {string} [reportId] The report it is printed for.
 * @returns {Object} A detached activity with only the report's draft fields replaced.
 */
export function applyHostReportDraft(activity = {}, draft = null, reportId) {
	const detached = _.cloneDeep(isPlainObject(activity) ? activity : {});
	const normalizedDraft = normalizeHostReportDraft(draft, reportId);
	return normalizedDraft ? { ...detached, ...normalizedDraft } : detached;
}
